import { useInfiniteQuery } from "@tanstack/react-query";
import { chatService, type SearchFilters, type SearchResponse } from "@/services/chat.service";
import { postsService } from "@/services/posts.service";
import { mapPostToFeed, type FeedPost } from "@/hooks/useRecentStashes";

export interface SearchStashesPage {
  posts: FeedPost[];
  total: number;
  hasMore: boolean;
  nextOffset?: number;
  currentOffset: number;
  tagCounts?: Record<string, number>;
}

export const mapSearchResultsToPosts = async (
  searchResults: NonNullable<SearchResponse["results"]>,
): Promise<FeedPost[]> => {
  if (searchResults.length === 0) {
    return [];
  }

  const idCandidates = searchResults
    .map(result => result.stasht_id || result.id)
    .filter((id): id is string => Boolean(id));

  if (idCandidates.length === 0) {
    return [];
  }

  const batchResponse = await postsService.batchGetPosts(idCandidates);
  const postsRecord = batchResponse.posts ?? {};
  const seen = new Set<string>();
  const orderedPosts: FeedPost[] = [];

  for (const result of searchResults) {
    const candidates = [result.stasht_id, result.id].filter((id): id is string => Boolean(id));
    for (const candidate of candidates) {
      const post = postsRecord[candidate];
      if (post && !seen.has(candidate)) {
        orderedPosts.push(mapPostToFeed(post));
        seen.add(candidate);
        break;
      }
    }
  }

  for (const [postId, post] of Object.entries(postsRecord)) {
    if (!seen.has(postId)) {
      orderedPosts.push(mapPostToFeed(post));
    }
  }

  return orderedPosts;
};

const hasActiveFilters = (filters?: SearchFilters) =>
  Boolean(
    filters &&
      Object.values(filters).some(value => {
        if (!value) return false;
        if (typeof value === "string") return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        return true;
      }),
  );

const serializeFilterList = (values?: string[]) => {
  if (!values) return null;
  const cleaned = values.map(value => value.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  return [...new Set(cleaned.map(value => value.toLowerCase()))].sort().join("|");
};

const searchStashesQueryKey = (query: string, filters?: SearchFilters) =>
  [
    "search",
    query,
    filters?.overall_message_intent ?? null,
    filters?.overall_message_domain ?? null,
    filters?.entity_domain ?? null,
    filters?.entity_category ?? null,
    filters?.category_iabv2_2_depth0 ?? null,
    filters?.category_iabv2_2_depth1 ?? null,
    filters?.category_iabv2_2_depth2 ?? null,
    serializeFilterList(filters?.category_iabv2_2_any),
  ] as const;

const createSearchStashesQueryFn =
  (query: string, pageSize: number = 20, filters?: SearchFilters) =>
  async ({ pageParam = 0 }: { pageParam?: number }): Promise<SearchStashesPage> => {
    const response = await chatService.searchStashes(query, pageParam, pageSize, { filters });
    const searchResults = response.results ?? [];
    const posts = await mapSearchResultsToPosts(searchResults);

    const pageLimit = response.limit ?? pageSize;
    const resolvedOffset = response.offset ?? pageParam;
    const nextResolvedOffset = resolvedOffset + pageLimit;

    const inferredHasMore =
      typeof response.has_more === "boolean"
        ? response.has_more
        : (() => {
            const total = response.total_found;
            if (typeof total === "number") {
              return nextResolvedOffset < total;
            }
            return (response.results?.length ?? 0) === pageLimit;
          })();

    return {
      posts,
      total: response.total_found ?? 0,
      hasMore: inferredHasMore,
      nextOffset: inferredHasMore ? nextResolvedOffset : undefined,
      currentOffset: resolvedOffset,
      tagCounts: response.tag_counts,
    };
};

export const createSearchStashesInfiniteOptions = (
  query: string,
  pageSize: number = 20,
  filters?: SearchFilters,
) => ({
  queryKey: searchStashesQueryKey(query, filters),
  initialPageParam: 0,
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  enabled: query.trim().length > 0 || hasActiveFilters(filters),
  queryFn: createSearchStashesQueryFn(query, pageSize, filters),
  getNextPageParam: (lastPage: SearchStashesPage) => {
    if (!lastPage.hasMore) {
      return undefined;
    }
    return lastPage.nextOffset;
  },
});

export const useSearchStashes = (query: string, pageSize: number = 20, filters?: SearchFilters, enabled = true) => {
  const options = createSearchStashesInfiniteOptions(query, pageSize, filters);
  return useInfiniteQuery({ ...options, enabled: enabled && options.enabled });
};
